import { Component, DestroyRef, inject, input, linkedSignal, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { WorkflowStatusType } from '../../../core/models/workflow-status-type';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { PatientPortalUserRequestsService } from '../../services/patient-portal-user-requests.service';

export interface RequestStatusSelection {
  status: WorkflowStatusType;
  label: string;
  /**
   * The request's new concurrency version returned by the SetStatus API, or
   * `null` when the envelope came back empty. Deliberately not defaulted to the
   * version this card sent: another mutation (an assign) may have advanced the
   * version while the save was in flight, and echoing the sent value back to
   * the parent would roll it backwards and fail the next write's concurrency
   * check. `null` means "no newer version to apply" — the parent keeps its own.
   */
  version: number | null;
}

interface StatusOption {
  status: WorkflowStatusType;
  label: string;
  description: string;
  badgeClass: string;
}

// How long the in-button "Saved" confirmation shows before the button
// returns to its idle label.
const CONFIRMATION_RESET_MS = 2500;

@Component({
  selector: 'app-request-status-card',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './request-status-card.html',
  styleUrl: './request-status-card.scss',
})
export class RequestStatusCard {
  private readonly destroyRef = inject(DestroyRef);
  private readonly requestsService = inject(PatientPortalUserRequestsService);

  readonly requestId = input.required<number>();
  readonly version = input.required<number>();
  /**
   * The request's stored workflow status id from the API. Ids that aren't a
   * staff-selectable option (e.g. New/Pending) leave nothing pre-selected.
   */
  readonly currentStatusTypeId = input<number | null>(null);

  readonly statusUpdated = output<RequestStatusSelection>();

  readonly statusOptions: StatusOption[] = [
    {
      status: WorkflowStatusType.InProgress,
      label: 'In Progress',
      description: 'Currently under review',
      badgeClass: 'badge--in-progress',
    },
    {
      status: WorkflowStatusType.NeedsInfo,
      label: 'Needs Info',
      description: 'Awaiting patient follow-up',
      badgeClass: 'badge--needs-info',
    },
  ];

  // Pre-selects the option matching the request's stored status; staff can
  // still change the selection locally before saving.
  readonly selectedStatus = linkedSignal<WorkflowStatusType | null>(() => {
    const current = this.currentStatusTypeId();
    const option = this.statusOptions.find((candidate) => candidate.status === current);
    return option?.status ?? null;
  });
  readonly isSaving = signal(false);
  readonly showSelectionError = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly justSaved = signal(false);

  private confirmationTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.confirmationTimeout !== null) {
        clearTimeout(this.confirmationTimeout);
      }
    });
  }

  selectStatus(status: WorkflowStatusType): void {
    if (this.isSaving()) {
      return;
    }

    this.selectedStatus.set(status);
    this.showSelectionError.set(false);
  }

  isStatusSelected(status: WorkflowStatusType): boolean {
    return this.selectedStatus() === status;
  }

  // Roving tabindex for the radio group: the selected option (or the first,
  // when nothing is selected yet) is the group's single tab stop.
  optionTabIndex(index: number): number {
    const selectedIndex = this.statusOptions.findIndex(
      (option) => option.status === this.selectedStatus(),
    );
    const activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    return index === activeIndex ? 0 : -1;
  }

  // Arrow keys move (and select) within the radio group, per the WAI-ARIA
  // radio pattern.
  onOptionKeydown(event: KeyboardEvent, index: number): void {
    let delta: number;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        delta = 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        delta = -1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextIndex = (index + delta + this.statusOptions.length) % this.statusOptions.length;
    this.selectStatus(this.statusOptions[nextIndex].status);

    const group = (event.currentTarget as HTMLElement).closest('[role="radiogroup"]');
    const radios = group?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[nextIndex]?.focus();
  }

  updateStatus(): void {
    if (this.isSaving()) {
      return;
    }

    const status = this.selectedStatus();
    if (status === null) {
      this.showSelectionError.set(true);
      return;
    }

    this.showSelectionError.set(false);
    this.saveError.set(null);
    this.justSaved.set(false);
    if (this.confirmationTimeout !== null) {
      clearTimeout(this.confirmationTimeout);
    }

    this.isSaving.set(true);

    this.requestsService
      .setStatus(this.requestId(), {
        patientPortalUserRequestWorkflowStatusTypeId: status,
        version: this.version(),
      })
      .pipe(
        // finalize (not the handlers) resets the busy flag so a throw inside
        // next can never leave the button disabled for good.
        finalize(() => {
          this.isSaving.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.justSaved.set(true);

          const option = this.statusOptions.find((candidate) => candidate.status === status);
          this.statusUpdated.emit({
            status,
            label: option?.label ?? '',
            // The API should return the request's new concurrency version. An
            // empty envelope emits null rather than the version we sent — see
            // RequestStatusSelection.version for why echoing it back would let
            // a concurrent assign's newer version be overwritten with an older
            // one.
            version: response?.version ?? null,
          });

          this.confirmationTimeout = setTimeout(() => {
            this.justSaved.set(false);
          }, CONFIRMATION_RESET_MS);
        },
        error: (error) => {
          logHttpError('Failed to update request status', error);
          this.saveError.set('Unable to update the status. Please try again.');
        },
      });
  }
}
